<template>
    <div>
        <v-row align="center" justify="center" no-gutters>
            <v-col align="center" justify="center">
                <img :src="require('@/assets/images/logoDark.png')" class="ma-0 pa-0" id="image" />
            </v-col>
        </v-row>

        <div v-if="charger">
            <v-card>
                <v-card-title class="py-xs-0">
                    {{ chargerName }}<template v-if="charger.name">&nbsp;({{ charger.name }})</template>
                    <v-spacer />
                    <v-btn icon small @click="forceDetails = !forceDetails" :title="forceDetails ? 'Menos informações' : 'Mais informações'">
                        <v-icon>{{ forceDetails ? "expand_less" : "info" }}</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-row align="center" justify="center" no-gutters>
                        <v-col cols="12" lg="6" v-if="forceDetails">
                            <div class="d-flex flex-wrap mb-2">
                                <v-chip small outlined class="mr-2 mb-1"><v-icon small left>ev_station</v-icon>{{ chargerType }}</v-chip>
                                <v-chip small outlined class="mb-1" v-if="polityLabel"><v-icon small left>{{ polityIcon }}</v-icon>{{ polityLabel }}</v-chip>
                            </div>
                            <p class="description mb-2" v-if="charger.description">{{ charger.description }}</p>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <v-alert :dense="$vuetify.breakpoint.xs" class="mt-1 mb-0" color="secondary" dark v-if="priceItems.length > 0">
                                <div class="text-overline">{{ isActivationCharge ? "Cobrança por ativação" : "Cobrança por consumo de energia" }}</div>
                                <div v-for="(item, index) in priceItems" :key="item.label">
                                    <v-divider v-if="index > 0" class="my-1" />
                                    <div class="d-flex justify-space-between align-start py-1">
                                        <div class="pr-2">
                                            <v-icon small left>{{ item.icon }}</v-icon>{{ item.label }}
                                            <div class="text-caption price-hint" v-if="item.hint">{{ item.hint }}</div>
                                            <div class="text-caption price-hint" v-if="forceDetails && item.details">{{ item.details }}</div>
                                        </div>
                                        <strong class="text-no-wrap">{{ item.value }}</strong>
                                    </div>
                                </div>
                                <div class="text-caption price-hint mt-2" v-if="forceDetails && isConsumptionCharge && hasCardPayment">
                                    Em pagamentos com cartão de crédito, o estorno pode levar até 2 dias úteis para aparecer na fatura.
                                </div>
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script>
import { money } from "@/helpers/paymentAlerts";

const POLITY_LABELS = {
    PUBLIC: "Público",
    PUBLIC_LOCKED: "Público · liberação por aplicativo",
    PUBLIC_LOCKED_WITH_PAYMENT: "Público · pagamento",
    CONDOMINIUM: "Uso restrito",
    CONDOMINIUM_WITH_PAYMENT: "Uso restrito · pagamento",
};

export default {

    props: {
        charger: {
            required: true,
        },
        chargerName: {
            type: String,
            required: true,
        },
        hideDetails: {
            type: Boolean,
        }
    },

    data() {
        return {
            forceDetails: false,
            hasCardPayment: false,
        };
    },

    computed: {
        chargerType() {
            const type = this.charger.model == "DC" ? "DC" : "AC";
            if (this.charger.brand === "INCHARGE" && this.charger.model && this.charger.model != type) {
                return `${type} · INCHARGE ${this.charger.model}`;
            }
            return type;
        },
        polityLabel() {
            return POLITY_LABELS[this.charger.polity] || this.charger.polity;
        },
        polityIcon() {
            return this.charger.polity == "PUBLIC" ? "lock_open" : "lock";
        },
        isActivationCharge() {
            return this.charger.pricePolicy == "ACTIVATION_CHARGE";
        },
        isConsumptionCharge() {
            return this.charger.pricePolicy == "PAYMENT_PER_CONSUMPTION";
        },
        priceItems() {
            if (this.isActivationCharge) {
                return [{ icon: "payment", label: "Valor fixo", value: money(this.charger.price) }];
            }
            if (!this.isConsumptionCharge) {
                return [];
            }

            const items = [
                {
                    icon: "account_balance_wallet",
                    label: "Caução",
                    hint: "Troco automático do valor não consumido",
                    details: "Valor reservado antes de ativar o carregador e ajustado ao final da recarga.",
                    value: money(this.charger.price),
                },
                { icon: "flash_on", label: "Energia", value: `${money(this.charger.priceKwh)}/kWh` },
            ];

            if (Number(this.charger.priceConnection) > 0) {
                const tolerance = Number(this.charger.connectionTolerance);
                items.push({
                    icon: "lock_open",
                    label: "Taxa de desbloqueio",
                    hint: tolerance > 0 ? `Cobrada se o carregamento durar mais de ${tolerance} min` : null,
                    details: "Valor fixo cobrado para iniciar o carregamento.",
                    value: money(this.charger.priceConnection),
                });
            }

            if (Number(this.charger.priceIdle) > 0) {
                const tolerance = Number(this.charger.idleTolerance);
                items.push({
                    icon: "timer",
                    label: "Ociosidade",
                    hint: tolerance > 0 ? `Após ${tolerance} min de tolerância` : null,
                    details: "Tempo em que o veículo permanece conectado após o fim do carregamento.",
                    value: `${money(this.charger.priceIdle)}/min`,
                });
            }

            return items;
        },
    },

    watch: {
        chargerName: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.getPayOption();
                }
            },
        },
        hideDetails: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.forceDetails) {
                    this.forceDetails = false;
                }
            },
        },
    },

    methods: {
        getPayOption() {
            this.$http
                .get("api/v2/payment-detail/" + this.chargerName)
                .then((result) => {
                    this.hasCardPayment = !!(result && (result.stripe || result.mercadopago || result.pagarme));
                })
                .catch(() => {
                    this.hasCardPayment = false;
                });
        },
    }
};
</script>
<style scoped>
.price-hint {
    opacity: 0.8;
}

.description {
    white-space: pre-line;
}

@media screen and (min-width: 1280px) {
    #image {
        max-width: 20vw;
    }
}

@media screen and (min-width: 1024px) and (max-width: 1279px) {
    #image {
        max-width: 30vw;
    }
}

@media screen and (min-width: 540px) and (max-width: 1023px) {
    #image {
        max-width: 40vw;
    }
}

@media screen and (max-width: 540px) {
    #image {
        max-width: 50vw;
    }
}
</style>